import * as React from 'react';
import { isEqual } from 'lodash';
import { apiHooks } from '../../../shared/api-clients/application';
import {
  BEEConfiguration,
  DeviceInfo,
  DeviceType,
  HeatingZoneID,
  RuleCategory,
} from '../../../shared/api-clients/_api';
import { useBee } from '../../../shared/hooks/useBee';

type KeysOfUnion<T> = T extends T ? keyof T : never;

export interface IBeeConfigContext {
  error: any;
  loading: boolean;
  devices: DeviceInfo[];
  getDevicesByType: <T extends DeviceInfo>(deviceTypes: DeviceType[]) => T[];
  getDevicesByZone: <T extends DeviceInfo>(zone: HeatingZoneID) => T[];
  getDevicesByCategory: <T extends DeviceInfo>(category: RuleCategory) => T[];
  createDevice: <T extends DeviceInfo>(...devices: T[]) => void;
  editDevice: <T extends DeviceInfo>(currentDevice: T, updatedDevice: T) => Promise<void>;
  removeDevice: <T extends DeviceInfo>(device: T) => void;
  addDevicesToConfig: <T extends DeviceInfo>(...devices: T[]) => Promise<void>;
  removeDevicesFromConfig: <T extends DeviceInfo>(...devices: T[]) => Promise<void>;
  pushDevicesToConfig: <T extends DeviceInfo>(devices?: T[]) => Promise<BEEConfiguration>;
  updateConfig: <A extends DeviceInfo, R extends DeviceInfo, E extends DeviceInfo>(changes: {
    devicesToRemove?: R[];
    devicesToAdd?: A[];
    devicesToEdit?: { currentDevice: E; updatedDevice: E }[];
  }) => Promise<BEEConfiguration>;
  getAssignedResourcesFor: <T>(
    deviceTypes: DeviceType[],
    paramKey: KeysOfUnion<DeviceInfo['params']>,
    ...currentResources: T[]
  ) => T[];
}

const INCOMPLETE_PARAMS = [undefined, null, ''];

const cleanParams = (deviceType: DeviceType, params: { [x: string]: any }) => {
  const clean = {};

  for (const key in params) {
    // To be fixed - https://sero-digital.atlassian.net/browse/IES-1384
    if (
      key === 'channel' &&
      deviceType !== DeviceType.MASTERTHERM &&
      !INCOMPLETE_PARAMS.includes(params[key]?.address)
    ) {
      clean[key] = params[key]?.address;
    } else if (key !== '__typename' && !INCOMPLETE_PARAMS.includes(params[key])) {
      clean[key] = params[key];
    }
  }

  return clean;
};

export const BeeConfigContext = React.createContext<IBeeConfigContext>(undefined);

/**
 * Parse rules in BEE config and transform into device info objects
 * @param config
 * @returns Array of device info
 */
const mapRulesToDeviceInfo = <T extends DeviceInfo>(config: BEEConfiguration) => {
  if (!config?.rules) {
    return [];
  }
  const existingDevices = config?.rules.reduce<T[]>((devices, rule) => {
    // device_type?: DeviceType;
    // zone_id?: HeatingZoneID;
    // address?: DeviceAddress;
    // channel?: DeviceChannel;
    // metadata?: DeviceRulesMetadata;
    const { device_type: deviceType, metadata = {}, ...inputs } = rule.inputs;

    return [
      ...devices,
      {
        deviceType,
        params: cleanParams(deviceType, {
          ...inputs,
          ...metadata,
          ...rule.metadata,
        }),
      } as unknown as T,
    ];
  }, []);
  return existingDevices;
};

export const hasCompleteParams = <T extends DeviceInfo>({ params }: T) =>
  Object.values(params).every((param: any) => !INCOMPLETE_PARAMS.includes(param));

export const BeeConfigProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const { beeInfo } = useBee();

  const [updateDeviceRules, { isFetching: isUpdatingConfig, data: updatedConfig, error: updateConfigError }] =
    apiHooks.config.updateDeviceRules.useMutation();

  const [devices, setDevices] = React.useState([]);

  React.useEffect(() => {
    if (beeInfo?.config) {
      setDevices(currentDevices => [
        ...mapRulesToDeviceInfo(beeInfo?.config),
        ...currentDevices.filter(d => !hasCompleteParams(d)),
      ]);
    }
  }, [beeInfo?.config]);

  React.useEffect(() => {
    if (updatedConfig) {
      setDevices(currentDevices => [
        ...mapRulesToDeviceInfo(updatedConfig),
        ...currentDevices.filter(d => !hasCompleteParams(d)),
      ]);
    }
  }, [updatedConfig]);

  const getDevicesByType = React.useCallback(
    <T extends DeviceInfo>(deviceTypes: DeviceType[]) => {
      return devices.filter(({ deviceType }) => deviceTypes.includes(deviceType)) as T[];
    },
    [devices]
  );

  const getDevicesByZone = React.useCallback(
    <T extends DeviceInfo>(zone: HeatingZoneID) =>
      devices.filter(({ params }) => (params as any)?.zone_id === zone) as T[],
    [devices]
  );

  const getDevicesByCategory = React.useCallback(
    <T extends DeviceInfo>(category: RuleCategory) =>
      devices.filter(({ params }) => (params as any)?.category === category) as T[],
    [devices]
  );

  const createDevice = React.useCallback<IBeeConfigContext['createDevice']>(
    (...devicesToAdd: DeviceInfo[]) => {
      setDevices(currentDevices => [...currentDevices, ...devicesToAdd]);
    },
    [devices]
  );

  const removeDevice = React.useCallback<IBeeConfigContext['removeDevice']>(
    device => {
      setDevices(currentDevices => currentDevices.filter(currentDevice => !isEqual(currentDevice, device)));
    },
    [devices]
  );

  const editDevice = React.useCallback<IBeeConfigContext['editDevice']>(
    async (device, updatedDevice) => {
      setDevices(existingDevices => {
        const existingDevicesUpdated = existingDevices.filter(currentDevice => !isEqual(currentDevice, device));
        existingDevicesUpdated.push(updatedDevice);
        return existingDevicesUpdated;
      });
    },
    [devices]
  );

  const addDevicesToConfig = React.useCallback<IBeeConfigContext['addDevicesToConfig']>(
    async (...devicesToAdd: DeviceInfo[]) => {
      await updateDeviceRules({
        deviceId: beeInfo.bee.deviceId,
        devices: [...devicesToAdd, ...devices.filter(hasCompleteParams)],
      });
    },
    [beeInfo, devices]
  );

  const removeDevicesFromConfig = React.useCallback<IBeeConfigContext['removeDevicesFromConfig']>(
    async (...devicesToRemove: DeviceInfo[]) => {
      await updateDeviceRules({
        deviceId: beeInfo.bee.deviceId,
        devices: [...devices.filter(hasCompleteParams)].filter(device => !devicesToRemove.includes(device)),
      });
    },
    [beeInfo, devices]
  );

  const pushDevicesToConfig = React.useCallback(
    (devicesToPush: DeviceInfo[] = devices) =>
      updateDeviceRules({
        deviceId: beeInfo.bee.deviceId,
        // only send devices with complete params
        devices: devicesToPush.filter(hasCompleteParams),
      }),
    [beeInfo, devices]
  );

  const updateConfig = React.useCallback<IBeeConfigContext['updateConfig']>(
    ({
      devicesToAdd = [],
      devicesToEdit = [],
      devicesToRemove = [],
    }: {
      devicesToRemove?: DeviceInfo[];
      devicesToAdd?: DeviceInfo[];
      devicesToEdit?: { currentDevice: DeviceInfo; updatedDevice: DeviceInfo }[];
    }) => {
      const existingDevicesUpdated = devices
        .filter(currentDevice => devicesToRemove.length === 0 || !devicesToRemove?.includes(currentDevice))
        .filter(
          currentDevice =>
            devicesToEdit.length === 0 ||
            devicesToEdit
              ?.map(editable => editable.currentDevice)
              .find(currentDeviceToRemove => !isEqual(currentDevice, currentDeviceToRemove))
        );
      devicesToEdit?.map(editable => editable.updatedDevice).forEach(device => existingDevicesUpdated.push(device));
      devicesToAdd?.forEach(device => existingDevicesUpdated.push(device));
      return updateDeviceRules({
        deviceId: beeInfo.bee.deviceId,
        // only send devices with complete params
        devices: existingDevicesUpdated.filter(hasCompleteParams),
      });
    },
    [beeInfo, devices]
  );

  const getAssignedResourcesFor = React.useCallback<IBeeConfigContext['getAssignedResourcesFor']>(
    (deviceTypes, paramKey, ...currentResources) => {
      // create deduped list of assigned resources (i.e relays, zones)
      const devicesByType = getDevicesByType(deviceTypes)
        .filter(device => (paramKey ? !!device.params[paramKey] : true))
        .map(device => (paramKey ? device.params[paramKey] : device));
      const assigned = Array.from(new Set(devicesByType));

      // exclude the current resources from assigned list
      return assigned.filter(resource => !currentResources.includes(resource));
    },
    [devices]
  );

  return (
    <BeeConfigContext.Provider
      value={{
        error: updateConfigError,
        loading: isUpdatingConfig,
        devices,
        getDevicesByType,
        getDevicesByZone,
        getDevicesByCategory,
        createDevice,
        removeDevice,
        editDevice,
        addDevicesToConfig,
        removeDevicesFromConfig,
        pushDevicesToConfig,
        updateConfig,
        getAssignedResourcesFor,
      }}
    >
      {children}
    </BeeConfigContext.Provider>
  );
};

export const useBeeConfig = () => React.useContext(BeeConfigContext);
